<template>
  <div
    class="px-4 py-12 mx-auto bg-white max-w-7xl sm:px-6 lg:px-8 dark:bg-zinc-950"
  >
    <!-- Page Header -->
    <header class="mb-12 space-y-4 text-center">
      <h1
        class="text-3xl font-extrabold text-gray-900 dark:text-zinc-100 sm:text-4xl"
      >
        {{ $t("contacts.mainTitle") }}
      </h1>
      <p class="max-w-2xl mx-auto text-lg text-gray-600 dark:text-zinc-300">
        {{ $t("contacts.description") }}
      </p>
    </header>

    <!-- Grid Layout -->
    <div class="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
      <!-- Left: Map + Address -->
      <div class="flex flex-col gap-6">
        <!-- Map -->
        <div
          class="rounded-xl overflow-hidden shadow-md border border-gray-300 dark:border-zinc-700 aspect-[4/3]"
        >
          <iframe
            class="w-full h-full"
            :src="mapSrc"
            frameborder="0"
            allowfullscreen
            loading="lazy"
            referrerpolicy="no-referrer-when-downgrade"
          ></iframe>
        </div>

        <!-- Address Card -->
        <div
          class="p-5 bg-white border border-gray-200 shadow-md rounded-xl dark:bg-zinc-800 dark:border-zinc-700"
        >
          <h2 class="text-xl font-bold text-gray-800 dark:text-zinc-100">
            Highfield Studio
          </h2>
          <address
            class="mt-2 space-y-1 text-base not-italic text-gray-700 dark:text-zinc-300"
          >
            <p>Estrada Jorge Brum do Canto (cineasta)</p>
            <p>Campo de Cima</p>
            <p>9400-127 Porto Santo</p>
          </address>
        </div>
      </div>

      <!-- Right: Contact + Schedule -->
      <div class="flex flex-col gap-6">
        <!-- Contact Methods -->
        <div
          class="p-5 bg-white border border-gray-200 shadow-md rounded-xl dark:bg-zinc-800 dark:border-zinc-700"
        >
          <h2 class="text-xl font-semibold text-gray-900 dark:text-zinc-100">
            {{ $t("contacts.getInTouch") }}
          </h2>
          <div class="grid grid-cols-1 gap-2 mt-4 sm:grid-cols-2">
            <button
              v-for="link in links"
              :key="link.name"
              @click="handleClick(link.link)"
              class="flex items-center gap-3 px-3 py-2 text-left bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 dark:bg-zinc-700 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-600"
            >
              <Icon
                :name="link.icon"
                class="text-xl text-gray-700 dark:text-zinc-300"
              />
              <span
                class="text-sm font-medium text-gray-800 dark:text-zinc-100"
                >{{ link.name }}</span
              >
            </button>
            <a
              :href="whatsappLink + encodedMessage"
              target="_blank"
              class="flex items-center gap-3 px-3 py-2 text-left bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-zinc-700 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-600"
            >
              <Icon
                name="logos:whatsapp-icon"
                class="text-xl text-gray-700 dark:text-zinc-300"
              />
              <span
                class="text-sm font-medium text-gray-800 dark:text-zinc-100"
              >
                WhatsApp
              </span>
            </a>
          </div>
        </div>

        <!-- Compact Schedule -->
        <div
          class="p-5 bg-white border border-gray-200 shadow-md rounded-xl dark:bg-zinc-800 dark:border-zinc-700"
        >
          <h2 class="text-xl font-semibold text-gray-900 dark:text-zinc-100">
            {{ $t("contacts.scheduleTitle") }}
          </h2>
          <div class="mt-3 space-y-2">
            <div
              v-for="{ key, label } in weekdays"
              :key="key"
              class="flex items-center justify-between py-1 text-sm"
            >
              <div class="flex items-center">
                <span
                  :class="
                    key === todayKey
                      ? 'font-medium text-gray-900 dark:text-zinc-100'
                      : 'text-gray-700 dark:text-zinc-300'
                  "
                >
                  {{ label }}
                </span>
                <span v-if="key === todayKey" class="flex items-center ml-2">
                  <span
                    class="w-2 h-2 mr-1 rounded-full"
                    :class="isOpenNow ? 'bg-green-500' : 'bg-red-500'"
                  ></span>
                </span>
              </div>
              <span
                :class="
                  key === todayKey
                    ? 'font-medium text-gray-900 dark:text-zinc-100'
                    : 'text-gray-700 dark:text-zinc-300'
                "
              >
                {{ schedule[key] }}
              </span>
            </div>
          </div>
          <p
            class="pt-2 mt-3 text-xs italic text-gray-500 border-t border-gray-100 dark:text-zinc-400 dark:border-zinc-700"
          >
            {{ $t("contacts.closedOnHolidays") }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useSchedule } from "~/data/schedule";

const { t } = useI18n();

// Links for contact section
const links = computed(() => [
  {
    name: t("contacts.call"),
    link: "tel:+351967998541",
    icon: "material-symbols:call",
  },
  {
    name: "Email",
    link: "mailto:studio@highfield.pt",
    icon: "material-symbols:mail",
  },
  {
    name: "Instagram",
    link: "https://www.instagram.com/highfield.pt/",
    icon: "ri:instagram-fill",
  },
  {
    name: "Facebook",
    link: "https://www.facebook.com/highfield.pt",
    icon: "simple-icons:facebook",
  },
  {
    name: "Linktree",
    link: "https://linktr.ee/highfield.pt",
    icon: "simple-icons:linktree",
  },
]);

const whatsappLink = "https://wa.me/351967998541?text=";
const encodedMessage = ref(encodeURIComponent(t("messages.whatsappMessage")));

// Map source
const mapSrc =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3209.672010464979!2d-16.3599904!3d33.0623337!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xc5fe724b052c065%3A0x51674e152ee0203!2sHighfield%20-%20Est%C3%BAdio%20de%20Grava%C3%A7%C3%A3o%2C%20Escola%20de%20M%C3%BAsica%20e%20Loja%20de%20Instrumentos%20Musicais%20em%20Porto%20Santo!5e0!3m2!1spt-PT!2spt!4v1717777777777!5m2!1spt-PT!2spt";

// Open link handler
const handleClick = (url: string) => {
  if (
    typeof url === "string" &&
    (url.startsWith("mailto:") || url.startsWith("tel:"))
  ) {
    window.location.href = url;
  } else if (typeof url === "string") {
    window.open(url, "_blank", "noopener");
  }
};

// Weekly schedule

const { schedule } = useSchedule();

// Weekday labels
const weekdays = computed(() => [
  { key: "monday", label: t("weekdays.monday") },
  { key: "tuesday", label: t("weekdays.tuesday") },
  { key: "wednesday", label: t("weekdays.wednesday") },
  { key: "thursday", label: t("weekdays.thursday") },
  { key: "friday", label: t("weekdays.friday") },
  { key: "saturday", label: t("weekdays.saturday") },
  { key: "sunday", label: t("weekdays.sunday") },
]);

// Get today's weekday key
const jsDayIndex = new Date().getDay(); // 0 = Sunday
const todayKey = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
][jsDayIndex];

// Check if current time is within a given time range
function isNowInTimeRange(timeRange: string): boolean {
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const nowFormatted = now.toTimeString().slice(0, 5);
  console.log(`🕒 Current time: ${nowFormatted} (${nowMinutes} minutes)`);

  const result = timeRange.split(",").some((range, index) => {
    const trimmedRange = range.trim();
    const [start, end] = trimmedRange.split("–");

    console.groupCollapsed(
      `⏱️ Checking range #${index + 1}: "${trimmedRange}"`
    );

    if (!start || !end) {
      console.warn("⚠️ Invalid range (missing start or end):", trimmedRange);
      console.groupEnd();
      return false;
    }

    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);

    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;

    const isOvernight = endMinutes <= startMinutes;
    console.log(`Parsed start: ${start} → ${startMinutes} minutes`);
    console.log(`Parsed end:   ${end} → ${endMinutes} minutes`);
    console.log(
      `Type: ${isOvernight ? "🌙 Overnight range" : "☀️ Normal range"}`
    );

    let isInRange = false;

    if (isOvernight) {
      isInRange = nowMinutes >= startMinutes || nowMinutes < endMinutes;
    } else {
      isInRange = nowMinutes >= startMinutes && nowMinutes < endMinutes;
    }

    console.log(`✅ Is current time in range? → ${isInRange}`);
    console.groupEnd();

    return isInRange;
  });

  console.log("🔍 Final result for time range check:", result);
  return result;
}

// Is the studio open now?
const isOpenNow = computed(() => {
  const todayHours = schedule.value[todayKey];
  console.log("Today key:", todayKey);
  console.log("Today's hours:", todayHours);

  if (!todayHours) {
    console.log("No hours set for today.");
    return false;
  }

  if (todayHours.toLowerCase().includes("closed")) {
    console.log("Today is marked as closed (string includes 'closed').");
    return false;
  }

  if (todayHours === t("contacts.closed")) {
    console.log(
      `Today's hours match the translation for 'closed':`,
      t("contacts.closed")
    );
    return false;
  }

  const result = isNowInTimeRange(todayHours);
  console.log(
    "Checking if current time is within range:",
    todayHours,
    "→",
    result
  );
  return result;
});

// SEO metadata
useSeoMeta({
  title: t("menuKeys.contacts"),
  description:
    "Get in touch with Highfield Studio in Porto Santo. Contact us by phone, email, or via social media and WhatsApp for all your music studio needs.",
  keywords:
    "contact, highfield studio, porto santo, music studio, call, email, social media, whatsapp",
  ogTitle: "Contact | Highfield Studio",
  ogDescription:
    "Get in touch with Highfield Studio in Porto Santo. Contact us by phone, email, or via social media and WhatsApp.",
  ogImage: "/images/logos/studio/1.jpg",
  twitterCard: "summary_large_image",
  twitterTitle: "Contact | Highfield Studio",
  twitterDescription:
    "Get in touch with Highfield Studio in Porto Santo. Contact us by phone, email, or via social media and WhatsApp.",
  twitterImage: "/images/logos/studio/1.jpg",
});
</script>
